import {
  DollarOutlined,
  DownloadOutlined,
  FileDoneOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Typography } from "antd";
import { StyledContentBackground } from "components/content-full-screen/content-full-screen";
import dayjs, { Dayjs } from "dayjs";
import {
  canConsolidatedReport,
  canDownloadAccountsReport,
  canpackageReportRoles,
  canWalletCustomerReportRoles,
  canWalletReportRoles,
  useUserContext,
} from "features/users";
import { useTranslation } from "react-i18next";
import { colors } from "styles";
import { DATE_FORMATS } from "utils/date";

import {
  CardWrapper,
  ContentHeaderWrapper,
  StyledAvatar,
  StyledButtonWrapper,
  StyledCard,
  StyledText,
} from "./reports.styles";

const { RangePicker: DateRangePicker } = DatePicker;

type ReportProps = {
  handleConsolidatedReport: () => void;
  handleAccountsReport: () => void;
  handleSalesReport: () => void;
  handleCancelReport: () => void;
  handleCustomizeAccountsReport: () => void;
  handlePackageReport: () => void;
  handleWalletReport: () => void;
  handleCustomerWalletReport: () => void;
  handleDate: (date: [Dayjs | null, Dayjs | null] | null) => void;
  loading: any;
};

export const Reports = ({
  handleDate,
  handleConsolidatedReport,
  handleAccountsReport,
  handleSalesReport,
  handleWalletReport,
  handleCancelReport,
  handleCustomizeAccountsReport,
  handlePackageReport,
  handleCustomerWalletReport,
  loading,
}: ReportProps) => {
  const { user } = useUserContext();
  const { t } = useTranslation();
  const disabledDate = (current: any) => {
    if (!current) return false;

    // Get the current month and year
    const today = dayjs();
    const currentYear = today.year();
    const currentMonth = today.month(); // 0 = Jan, 1 = Feb, ..., 11 = Dec

    // Allowed months: Previous, Current, Next
    const allowedMonths = [currentMonth - 1, currentMonth, currentMonth + 1];

    // Enable only the previous, current, and next month of the same year
    return !(
      current.year() === currentYear && allowedMonths.includes(current.month())
    );
  };

  return (
    <>
      <StyledContentBackground>
        <ContentHeaderWrapper>
          <div>{t("reports.title")}</div>
          {canConsolidatedReport(user) ? (
            <div>
              <DateRangePicker
                allowEmpty={[true, true]}
                format={DATE_FORMATS.date}
                size="large"
                onChange={handleDate}
                disabledDate={disabledDate}
              />
            </div>
          ) : (
            <div>
              <DateRangePicker
                allowEmpty={[true, true]}
                format={DATE_FORMATS.date}
                size="large"
                onChange={handleDate}
              />
            </div>
          )}
        </ContentHeaderWrapper>
        <CardWrapper className="crd_list">
          {canConsolidatedReport(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileTextOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>{t("reports.consolidatedReport")}</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleConsolidatedReport}
                  loading={loading.loading3}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileTextOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>{t("reports.consolidatedReport")}</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleConsolidatedReport}
                  loading={loading.loading3}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          )}

          {!canConsolidatedReport(user) && canDownloadAccountsReport(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={<DollarOutlined style={{ color: colors.colorPrimary }} />}
              />
              <StyledText>{t("reports.accountsReport")}</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleAccountsReport}
                  loading={loading.loading1}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : null}
          {!canConsolidatedReport(user) && canDownloadAccountsReport(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileDoneOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>{t("reports.salesReport")}</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleSalesReport}
                  type="primary"
                  loading={loading.loading}
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : null}
          {canConsolidatedReport(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileTextOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>{t("reports.CancelReport")}</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleCancelReport}
                  loading={loading.loading2}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : null}
          {canConsolidatedReport(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileTextOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>Customize Account Report</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleCustomizeAccountsReport}
                  loading={loading.loading4}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : null}
          {canpackageReportRoles(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileTextOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>Package Report</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handlePackageReport}
                  loading={loading.loading5}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : null}
          {canWalletReportRoles(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileTextOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>Wallet Report</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleWalletReport}
                  loading={loading.loading6}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : null}
          {canWalletCustomerReportRoles(user) ? (
            <StyledCard>
              <StyledAvatar
                icon={
                  <FileTextOutlined style={{ color: colors.colorPrimary }} />
                }
              />
              <StyledText>Customer Wallet Report</StyledText>
              <StyledButtonWrapper className="reprt_btn">
                <Typography.Text strong style={{ fontSize: "1.2rem" }}>
                  {t("reports.xlsFile")}
                </Typography.Text>
                <Button
                  onClick={handleCustomerWalletReport}
                  loading={loading.loading7}
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  {t("reports.get")}
                </Button>
              </StyledButtonWrapper>
            </StyledCard>
          ) : null}
        </CardWrapper>
      </StyledContentBackground>
    </>
  );
};
